import {emailValidationRules, nationalPhoneNumberValidationRules} from "../../utils/validationRules";
import {getStoredSession} from "../../LocalStorageService";
import {isPlainDict} from "../../utils/utils";
import {rolesList} from "../../views/Team/userListVariables";
import countryList from "react-select-country-list";

/*
 * The Sign-in Method question, shared by Add New User (below) and the Primary Contact on Account Creation
 * (UserBadge), so that the two ways of registering somebody ask it with the same words and the same default.
 */
export const SIGN_IN_METHOD_OPTIONS = [
    {label: 'Email and password', value: false},
    {label: 'Microsoft work account', value: true}
];

export const SIGN_IN_METHOD_HINT = "Microsoft work account requires the customer's Microsoft administrator to have "
    + "approved Tape Ark first. Pick Email and password for anyone without an account in their directory, such as "
    + "a contractor or an auditor.";

export const TITLE_OPTIONS = [
    {label: "Mr",   value: "mr"},
    {label: "Mrs",  value: "mrs"},
    {label: "Ms",   value: "ms"},
    {label: "Miss", value: "miss"},
    {label: "Dr",   value: "dr"},
    {label: "Prof", value: "prof"},
];

const countryData = countryList().getData();


// {
//         sectionName: "",
//         label: "",
//         valueProperty: "",
//         type: "",
//         options: {
//             source: "",
//             displayKey: "",
//             valueKey: "",
//             isSingleOption: true,
//             unit: "",
//             decimals: 0,
//             defValue: "",
//             dateFormat: "",
//             validationRules: {},
//             isControlled: false,
//             isDisabled: false,
//             xs: 3 // in case of sizeInput, xs 6 is recommended minimum
//         }
//     },

// Ex:
// {
//         sectionName: "Test Section",
//         label: "Size Input Field",
//         valueProperty: "size_input_field",
//         type: "sizeInput",
//         options: {
//             unit: "GB",
//             decimals: 0,
//             defValue: "",
//             validationRules: {required: "Size Input is required"},
//             xs: 6
//         }
//     },

// Ex:
// {
//         sectionName: "Test Section",
//         label: "Date Picker",
//         valueProperty: "date",
//         type: "dateInput",
//         options: {
//             dateFormat: "YYYY/MMM/D",
//             validationRules: {required: "Date Input is required"},
//             xs: 6
//         }
//     },

// Ex:
// {
//         sectionName: "Test Section",
//         label: "Togller",
//         valueProperty: "toggle_val",
//         type: "toggleInput",
//         options: {
//             validationRules: {required: "Toggle Input is required"},
//             xs: 6
//         }
//     },

const rolesRanking = {
    admin: 4,
    owner: 3,
    creator: 2,
    member: 1
};

export const roleOptionValidator = (optionValue, valueKey, otherFieldWatchedValue, processedData) => { // Export for testing
    if (isPlainDict(optionValue) && valueKey === undefined) throw new Error("If optionValue is a dictionary, valueKey must be provided.");
    const option_value = isPlainDict(optionValue) ? optionValue[valueKey] : optionValue;
    const requesterRoleRanking = rolesRanking[getStoredSession().user.role ?? "creator"];
    const optionRoleRanking = rolesRanking[option_value ?? "creator"];
    const isEditMode = Object.keys(processedData).length > 0;
    const isCompanyChange = isEditMode && processedData.customer !== otherFieldWatchedValue;

    // console.log("roleOptionValidator:", {
    //     optionValue,
    //     valueKey,
    //     option_value,
    //     requesterRoleRanking,
    //     optionRoleRanking,
    //     currentUserRole: getStoredSession().user.role,
    //     otherFieldWatchedValue,
    //     isEditMode,
    //     isCompanyChange,
    //     processedData
    // });

    /*
     * The role the record already holds always stays in the list.
     *
     * Reported as: "why only one option there. IT should ideally filled with registered data and has an
     * option to switch." Edit User on a TapeArk user whose role is not admin offered Super Admin alone, so
     * the box meant to show their role showed nothing — MUI renders a select empty when its value is not
     * among its options, and logs "out-of-range value" — and Submit stayed disabled on a required field the
     * operator had no way to answer. Nothing about that user could be edited, their role least of all.
     *
     * This is not a relaxation of the three rules below: those still decide what a role may be changed TO.
     * It only keeps what it currently IS on offer, which is the one option a dropdown editing a value cannot
     * be without. It does mean an operator can re-select, and therefore change away from, a role ranked
     * above their own — reachable only for someone who can already list such a user, which today means an
     * admin looking at admins.
     *
     * Only while the company is still the one on the record. Move the user to another company and the role
     * they arrived with stops being a fact about them: keeping it on offer would let a TapeArk Super Admin be
     * dragged into a customer company still holding the admin role, which is exactly what checkpoints 2 and
     * 3 exist to refuse. A changed company empties the list, as it did before — the operator has to save the
     * move and reopen, and the record they reopen shows its own role again.
     */
    if (isEditMode && !isCompanyChange && processedData.role !== undefined && option_value === processedData.role) {
        return true;
    }

    // if TapeArk is selected customer, only admin can be assigned
    if (otherFieldWatchedValue !== undefined && otherFieldWatchedValue === "tapeark" && optionRoleRanking !== 4) {
        // console.log("Fail Checkpoint 1");
        return false;
    }

    // if Other customer is selected, admin role cannot be assigned
    if (otherFieldWatchedValue !== undefined && otherFieldWatchedValue !== "tapeark" && optionRoleRanking === 4) {
        // console.log("Fail Checkpoint 2");
        return false;
    }

    if (isEditMode && processedData.customer === "tapeark" && isCompanyChange) {
        // console.log("Fail Checkpoint 3");
        return false;
    } // in edit mode, if company is changed from tapeark to something else, role cannot be changed

    return optionRoleRanking <= requesterRoleRanking;
};

const userListConfig = [
    {
        sectionName: "",
        label: "Salutation",
        valueProperty: "title",
        type: "multiselect",
        options: {
            source: TITLE_OPTIONS,
            displayKey: "label",
            valueKey: "value",
            xs: 3,
            // validationRules: {required: "Salutation is required"}
            validationRules: {}
        }
    },
    {
        sectionName: "",
        label: "First Name",
        valueProperty: "first_name",
        type: "text",
        options: {
            xs: 9,
            validationRules: {required: "First Name is required"}
        }
    },
    {
        sectionName: "",
        label: "Last Name",
        valueProperty: "last_name",
        type: "text",
        options: {
            validationRules: {required: "Last Name is required"}
        }
    },
    {
        sectionName: "Contact Details",
        // How this person signs in, which decides which of two addresses they are emailed. It is not a
        // permission: the API accepts a Microsoft token from an allowlisted directory whatever is picked here.
        //
        // In Contact Details and above Email Address on purpose. The choice made here is what the address below
        // has to satisfy — a Microsoft work account has to be typed as that person's exact directory sign-in
        // name, or the token arrives with a username no user in Nexus matches. Asking which path first and the
        // address second is the order the operator has to think in; it read as an afterthought under Role Details.
        label: "Sign-in Method",
        valueProperty: "entra_login",
        type: "radioInput",
        options: {
            // Password first, and that ordering is the create-mode default — see the radioInput case in
            // CreateEditDialog. It is the path that works for every company, with or without their Microsoft
            // administrator having approved anything, so it is the safe thing to land on.
            source: SIGN_IN_METHOD_OPTIONS,
            xs: 12,
            hint: SIGN_IN_METHOD_HINT,
        }
    },
    {
        sectionName: "Contact Details",
        label: "Email Address",
        valueProperty: "email",
        type: "text",
        options: {
            validationRules: {...emailValidationRules, required: "Email Address is required"},
        }
    },
    {
        sectionName: "Contact Details",
        label: "Phone Number",
        valueProperty: "phone_number",
        // Country code picked from a list, national number typed beside it — the same pair of boxes the
        // Account step's Primary Contact uses, and the reason the rules below are the national ones. The
        // old combined pattern only ever recognised +61 and +1, so a correctly written number from anywhere
        // else was a validation error on a field that is not even mandatory.
        type: "dialledNumberInput",
        options: {
            // Optional, and stays optional: Mobile is the number this portal insists on. The pattern admits
            // the empty string, so a blank Phone is not what stops a Submit.
            validationRules: {...nationalPhoneNumberValidationRules},
        }
    },
    {
        sectionName: "Contact Details",
        label: "Mobile Number",
        valueProperty: "mobile_number",
        type: "dialledNumberInput",
        options: {
            // The one required number on this form, stated here rather than inherited from
            // mobileNumberValidationRules, whose pattern expects the country code inside the string.
            validationRules: {...nationalPhoneNumberValidationRules, required: "Mobile Number is required"},
            // Mandatory for a new user, not for an existing one. Until nexus-api was fixed, both user
            // serializers dropped mobile_number without saying so, so no user on file has one — and a
            // required field nobody can satisfy from the record in front of them disables Submit, which
            // meant an operator could not change a role or a job type without inventing a mobile number.
            requiredOnlyWhenCreating: true,
        }
    },
    {
        sectionName: "Role Details",
        label: "Role",
        valueProperty: "role",
        type: "multiselect",
        options: {
            source: rolesList,
            displayKey: "label",
            valueKey: "value",
            defValue: "creator",
            validationRules: {required: "Role is required"},
            optionValidator: roleOptionValidator,
            optionValidatorDependencyFieldName: "customer",
            xs: 6,
            // Names the roles exactly as the dropdown beside it does — see rolesList in userListVariables.
            hint: "Admin/Owner: full access including team management and data destruction.\nJob Owners: job tracking and data restore only.",
        }
    },
    {
        sectionName: "Role Details",
        label: "Job Type",
        valueProperty: "job_type",
        type: "multiselect",
        options: {
            source: [
                {label: 'Permanent Employee', value: 'permanent'},
                {label: 'Contractor', value: 'contractor'}
            ],
            displayKey: "label",
            valueKey: "value",
            validationRules: {required: "Job Type is required"},
            xs: 6
        }
    },
];


const customerUserConfig = [
    {
        sectionName: "",
        label: "Salutation",
        valueProperty: "title",
        type: "multiselect",
        options: {
            source: TITLE_OPTIONS,
            displayKey: "label",
            valueKey: "value",
            isSingleOption: true,
            xs: 3,
            validationRules: {}
        }
    },
    {
        sectionName: "",
        label: "First Name",
        valueProperty: "first_name",
        type: "text",
        options: {xs: 9, validationRules: {required: "First Name is required"}}
    },
    {
        sectionName: "",
        label: "Last Name",
        valueProperty: "last_name",
        type: "text",
        options: {validationRules: {required: "Last Name is required"}}
    },
    {
        sectionName: "Contact Details",
        label: "Email Address",
        valueProperty: "email",
        type: "text",
        options: {validationRules: {...emailValidationRules, required: "Email Address is required"}}
    },
    {
        sectionName: "Contact Details",
        label: "Country",
        valueProperty: "country",
        type: "multiselect",
        options: {
            source: countryData,
            displayKey: "label",
            valueKey: "value",
            isSingleOption: true,
            xs: 4,
            validationRules: {}
        }
    },
    {
        sectionName: "Contact Details",
        label: "Phone Number",
        valueProperty: "phone_number",
        // Same pair of boxes as on the userList config above; see the note there.
        type: "dialledNumberInput",
        // Full width now that the row carries a code dropdown as well as the digits.
        options: {validationRules: {...nationalPhoneNumberValidationRules}}
    },
    {
        sectionName: "Contact Details",
        label: "Mobile Number",
        valueProperty: "mobile_number",
        type: "dialledNumberInput",
        options: {
            validationRules: {...nationalPhoneNumberValidationRules, required: "Mobile Number is required"},
            // Same reasoning as on the userList config above.
            requiredOnlyWhenCreating: true,
        }
    },
    {
        sectionName: "Role Details",
        label: "Role",
        valueProperty: "role",
        type: "multiselect",
        options: {
            source: rolesList.filter(r => r.value !== 'all'),
            displayKey: "label",
            valueKey: "value",
            isSingleOption: true,
            xs: 6,
            validationRules: {required: "Role is required"},
            // Names the roles exactly as the dropdown beside it does — see rolesList in userListVariables.
            hint: "Admin/Owner: full access including team management and data destruction.\nJob Owners: job tracking and data restore only.",
        }
    },
    {
        sectionName: "Role Details",
        label: "Job Type",
        valueProperty: "job_type",
        type: "multiselect",
        options: {
            source: [
                {label: "Permanent Employee", value: "permanent"},
                {label: "Contractor", value: "contractor"},
            ],
            displayKey: "label",
            valueKey: "value",
            isSingleOption: true,
            xs: 6,
            validationRules: {required: "Job Type is required"}
        }
    },
];

export const dialogBoxConfig = {
    defValue: [],
    userList: userListConfig,
    customerUser: customerUserConfig,
};